import { useState } from 'react';
import { ROLES, ROLE_LABELS, type Role, type User, type UserCreateInput } from '@stromo/shared';
import { useCreateUser, useDeleteUser, useUpdateUser, useUsers } from '../lib/users';
import { useAuthStore } from '../lib/authStore';
import { apiErrorMessage } from '../lib/api';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface UserForm {
  abbreviation: string;
  first_name: string;
  last_name: string;
  email: string;
  role: Role;
  is_active: boolean;
  password: string;
}

const emptyForm = (): UserForm => ({
  abbreviation: '',
  first_name: '',
  last_name: '',
  email: '',
  role: 'site_manager',
  is_active: true,
  password: '',
});

export function UsersPage() {
  const { user: actingUser } = useAuthStore();
  const users = useUsers();
  const createMut = useCreateUser();
  const updateMut = useUpdateUser();
  const deleteMut = useDeleteUser();

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [form, setForm] = useState<UserForm>(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const open = (target?: User) => {
    if (target) {
      setEditTarget(target);
      setForm({
        abbreviation: target.abbreviation,
        first_name: target.first_name,
        last_name: target.last_name,
        email: target.email,
        role: target.role,
        is_active: target.is_active,
        password: '',
      });
    } else {
      setEditTarget(null);
      setForm(emptyForm());
    }
    setShowForm(true);
  };

  const close = () => {
    setShowForm(false);
    setEditTarget(null);
    createMut.reset();
    updateMut.reset();
  };

  const save = () => {
    if (editTarget) {
      const patch: Partial<UserCreateInput> = {
        abbreviation: form.abbreviation,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        role: form.role,
        is_active: form.is_active,
      };
      if (form.password) patch.password = form.password;
      updateMut.mutate({ id: editTarget.id, patch }, { onSuccess: close });
    } else {
      const input: UserCreateInput = {
        abbreviation: form.abbreviation,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        role: form.role,
        is_active: form.is_active,
        password: form.password,
      };
      createMut.mutate(input, { onSuccess: close });
    }
  };

  const saveError = createMut.isError
    ? apiErrorMessage(createMut.error)
    : updateMut.isError
    ? apiErrorMessage(updateMut.error)
    : null;

  const passwordRequired = !editTarget;
  const formValid =
    form.abbreviation.trim() &&
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.email.trim() &&
    (!passwordRequired || form.password.length >= 8);

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Benutzer</div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-primary" onClick={() => open()}>
            + Neuer Benutzer
          </button>
        </div>
      </header>

      <div className="content">
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          {users.isLoading ? (
            <div className="loading">Lade …</div>
          ) : (users.data ?? []).length === 0 ? (
            <div className="empty-state"><p>Keine Benutzer vorhanden.</p></div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Kürzel</th>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Rolle</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((u) => (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                    <td data-label="Kürzel" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{u.abbreviation}</td>
                    <td data-label="Name" style={{ color: 'var(--color-navy)', fontWeight: 600 }}>
                      {u.first_name} {u.last_name}
                    </td>
                    <td data-label="E-Mail">{u.email}</td>
                    <td data-label="Rolle">{ROLE_LABELS[u.role]}</td>
                    <td data-label="Status">
                      <span className={`badge ${u.is_active ? 'badge-progress' : 'badge-open'}`}>
                        {u.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td data-label="" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(u)}>
                        Bearbeiten
                      </button>
                      {u.id !== actingUser?.id && u.is_active && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteTarget(u)}
                          title="Deaktivieren"
                        >
                          Deaktivieren
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        open={showForm}
        title={editTarget ? `Benutzer: ${editTarget.first_name} ${editTarget.last_name}` : 'Neuer Benutzer'}
        onClose={close}
        width={560}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={close}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={save}
              disabled={!formValid || createMut.isPending || updateMut.isPending}
            >
              {createMut.isPending || updateMut.isPending
                ? 'Speichere …'
                : editTarget
                ? 'Speichern'
                : 'Anlegen'}
            </button>
          </>
        }
      >
        {saveError && <div className="login-error">{saveError}</div>}
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Kürzel *</label>
            <input
              value={form.abbreviation}
              onChange={(e) => setForm({ ...form, abbreviation: e.target.value.toUpperCase() })}
              maxLength={10}
              placeholder="z.B. PABR"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Rolle</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Vorname *</label>
            <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Nachname *</label>
            <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div className="form-group full">
            <label className="form-label">E-Mail *</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-group full">
            <label className="form-label">
              Passwort {passwordRequired ? '*' : '(leer = unverändert)'}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={passwordRequired ? 'Mindestens 8 Zeichen' : '••••••••'}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group full">
            <label style={{ fontSize: 13, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0 }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                style={{ width: 16, height: 16 }}
              />
              Aktiv (Login möglich)
            </label>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Benutzer deaktivieren"
        danger
        message={
          deleteTarget
            ? `Benutzer ${deleteTarget.first_name} ${deleteTarget.last_name} deaktivieren? Login wird gesperrt, Datensätze bleiben erhalten.`
            : ''
        }
        confirmLabel="Deaktivieren"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMut.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }
        }}
      />
    </>
  );
}
